import { createContext, useContext, useLayoutEffect } from 'react';

interface SecondarySidebarState {
	collapsed: boolean;
	expand: () => void;
	/** Set while a page shows the expand button itself. */
	setPageHasToggle: (has: boolean) => void;
}

export const SecondarySidebarContext = createContext<SecondarySidebarState | null>(null);

/**
 * For full-bleed pages (the workflow graph): the page puts the sidebar's expand button in
 * its own header, and the layout drops its floating button and the gutter kept for it —
 * a strip that reads as a gap against a canvas. Null outside a secondary-sidebar layout.
 */
export function useSecondarySidebarToggle(): Pick<SecondarySidebarState, 'collapsed' | 'expand'> | null {
	const sidebar = useContext(SecondarySidebarContext);
	const setPageHasToggle = sidebar?.setPageHasToggle;

	// Before paint, so the gutter never flashes on the way in.
	useLayoutEffect(() => {
		if (!setPageHasToggle) return;
		setPageHasToggle(true);
		return () => setPageHasToggle(false);
	}, [setPageHasToggle]);

	return sidebar && { collapsed: sidebar.collapsed, expand: sidebar.expand };
}
